import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiGet } from '../shared/api'
import Player from '../shared/Player'
import { useI18n } from '../i18n'

interface Episode {
  id: string
  title: string
  episode?: string
  season?: string
}

interface DetailData {
  id: string
  title: string
  poster: string
  desc: string
  year?: string
  type?: string
  episodes: Episode[]
}

interface PlayData {
  url: string
  type: 'hls' | 'mp4'
  headers?: Record<string, string>
}

export default function Detail() {
  const { siteId, id } = useParams()
  const [data, setData] = useState<DetailData | null>(null)
  const [currentEp, setCurrentEp] = useState<Episode | null>(null)
  const [play, setPlay] = useState<PlayData | null>(null)
  const [loading, setLoading] = useState(true)
  const [playLoading, setPlayLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)
  const playerBoxRef = useRef<HTMLDivElement | null>(null)
  const posterImgRef = useRef<HTMLImageElement | null>(null)
  const { t } = useI18n()

  useEffect(() => {
    setData(null)
    setCurrentEp(null)
    setPlay(null)
    setLoading(true)
    setPage(1)
    
    apiGet(`/api/sites/${siteId}/detail?id=${id}`).then((res) => {
      setData(res)
      setCurrentEp(res.episodes?.[0] || null)
      setLoading(false)
    }).catch((error) => {
      console.error('Failed to load detail:', error)
      setLoading(false)
    })
  }, [siteId, id])

  useEffect(() => {
    if (!currentEp) return
    
    setPlayLoading(true)
    apiGet(`/api/sites/${siteId}/play?episodeId=${encodeURIComponent(currentEp.id)}`).then((res) => {
      // Proxy M3U8 URLs through our HLS endpoint to avoid CORS and handle 404 issues
      if (res.url && (res.url.includes('.m3u8') || res.type === 'hls')) {
        // 优先使用 /hls 端点，它有更强大的重试和区域选择机制
        const proxiedUrl = `/hls?url=${encodeURIComponent(res.url)}`;
        setPlay({ ...res, url: proxiedUrl, type: 'hls' });
      } else if (res.url && res.url.includes('http')) {
        // 对于其他 HTTP 资源，使用 /proxy 端点
        const proxiedUrl = `/proxy?url=${encodeURIComponent(res.url)}`;
        setPlay({ ...res, url: proxiedUrl });
      } else {
        setPlay(res);
      }
      setPlayLoading(false);
    }).catch((error) => {
      console.error('Failed to load play data:', error);
      setPlayLoading(false);
    });
  }, [siteId, currentEp]);

  // 同步右侧海报图高度为播放器高度（桌面端）
  useEffect(() => {
    const playerEl = playerBoxRef.current
    const posterEl = posterImgRef.current
    if (!playerEl || !posterEl) return

    const sync = () => {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches
      if (!isDesktop) {
        posterEl.style.height = ''
        return
      }
      const height = playerEl.clientHeight
      if (height > 0) {
        posterEl.style.height = `${height}px`
      }
    }

    // 监听播放器容器尺寸变化
    const ro = new ResizeObserver(() => sync())
    ro.observe(playerEl)
    window.addEventListener('resize', sync)

    // 初始同步
    sync()

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', sync)
    }
  }, [playLoading, play])

  const eps = useMemo(() => data?.episodes || [], [data])
  const totalPages = useMemo(() => Math.max(1, Math.ceil(eps.length / pageSize)), [eps.length, pageSize])
  const pagedEps = useMemo(() => {
    const start = (page - 1) * pageSize
    return eps.slice(start, start + pageSize)
  }, [eps, page, pageSize])

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <div className="mt-2 text-gray-600">{t('common.loadingDetail')}</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❌</div>
        <div className="text-gray-500">{t('common.loadFailed')}</div>
        <Link to="/" className="text-blue-600 hover:underline mt-2 inline-block">
          {t('common.backHome')}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 面包屑导航 */}
      <nav className="text-sm text-gray-500">
        <Link to="/" className="hover:text-blue-600">{t('detail.breadcrumbHome')}</Link>
        <span className="mx-2">/</span>
        <span>{data.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 左侧：播放器和选集 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 播放器 */}
          <div className="bg-black rounded-lg overflow-hidden shadow-lg">
            <div className="aspect-video" ref={playerBoxRef}>
              {playLoading ? (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-white mb-2"></div>
                    <div>{t('detail.loadingPlayer')}</div>
                  </div>
                </div>
              ) : (
                <Player src={play?.url} type={play?.type} headers={play?.headers} className="w-full h-full" />
              )}
            </div>
          </div>

          {/* 选集列表 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('detail.episodes')}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>{t('detail.pageInfo', { page, total: totalPages })}</span>
                <button
                  className="px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >{t('common.prevPage')}</button>
                <button
                  className="px-2 py-1 rounded border hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >{t('common.nextPage')}</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {pagedEps.map((ep) => (
                <button
                  key={ep.id}
                  onClick={() => setCurrentEp(ep)}
                  disabled={playLoading}
                  className={`
                    px-4 py-3 rounded-lg border text-left transition-all duration-200 disabled:opacity-50
                    ${currentEp?.id === ep.id
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-blue-300'
                    }
                  `}
                >
                  <div className="font-medium text-sm">{ep.title}</div>
                  {ep.episode && (
                    <div className="text-xs opacity-75 mt-1">
                      {ep.season ? `S${ep.season}` : ''}E{ep.episode}
                    </div>
                  )}
                </button>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button
                  className="px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >{t('common.firstPage')}</button>
                <button
                  className="px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >{t('common.prevPage')}</button>
                <button
                  className="px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >{t('common.nextPage')}</button>
                <button
                  className="px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-50"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                >{t('common.lastPage')}</button>
              </div>
            )}
          </div>
        </div>

        {/* 右侧：信息面板 */}
        <div className="space-y-6">
          {/* 海报和基本信息 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <img
              ref={posterImgRef}
              src={data.poster}
              alt={data.title}
              className="w-full object-cover aspect-[2/3] lg:aspect-auto"
            />
            <div className="p-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-3">{data.title}</h1>
              
              <div className="flex items-center gap-4 mb-4">
                {data.year && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                    📅 {data.year}
                  </span>
                )}
                {data.type && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    🎬 {data.type}
                  </span>
                )}
              </div>
              
              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                {data.desc}
              </p>
            </div>
          </div>

          {/* 当前播放信息 */}
          {currentEp && (
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <h4 className="font-medium text-blue-900 mb-2">{t('detail.currentlyPlaying')}</h4>
              <div className="text-blue-800">
                <div className="font-medium">{currentEp.title}</div>
                {currentEp.episode && (
                  <div className="text-sm opacity-75">
                    {currentEp.season
                      ? `${t('detail.seasonLabel', { num: currentEp.season })} ${t('detail.episodeLabel', { num: currentEp.episode })}`
                      : t('detail.episodeLabel', { num: currentEp.episode })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
